import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { ProductFormCard, ProductFormData } from '@/components/seller/products/ProductFormCard';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { useAuth } from '@/contexts/AuthContext';

interface ProductEditData extends ProductFormData {
  id: string;
  selected: boolean;
}

export function DesktopProductEditPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();
  const { user } = useAuth();

  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductEditData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        
        // 판매자 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          showToast(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }
        
        setRestaurantId(restaurantResult.restaurantId);

        // 상품 목록 가져오기
        const productsResult = await sellerService.getSellerProducts(restaurantResult.restaurantId);
        if (productsResult.success && productsResult.data) {
          const editableProducts: ProductEditData[] = productsResult.data.map(product => ({
            id: product.id,
            name: product.name,
            description: product.description || '',
            category: product.category || '',
            originalPrice: product.original_price,
            stock: product.stock ?? 0, // DB에서 가져온 기본재고(없으면 0)
            imageFile: null,
            imageUrl: product.image_url,
            selected: false
          }));
          
          setProducts(editableProducts);
        }
        
      } catch (error) {
        console.error('Load products error:', error);
        showToast('상품 목록을 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [showToast]);

  const handleProductChange = (productId: string, updatedProduct: ProductFormData) => {
    setProducts(prev => prev.map(p => 
      p.id === productId 
        ? { ...p, ...updatedProduct }
        : p
    ));
  };

  const handleProductSelect = (productId: string, selected: boolean) => {
    setProducts(prev => prev.map(p => 
      p.id === productId 
        ? { ...p, selected }
        : p
    ));
  };

  const handleSelectAll = (selectAll: boolean) => {
    setProducts(prev => prev.map(p => ({ ...p, selected: selectAll })));
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedProducts = products.filter(p => p.selected);
  const canSave = selectedProducts.length > 0;
  const canDelete = selectedProducts.length > 0;
  const allSelected = products.length > 0 && products.every(p => p.selected);

  const handleSave = async () => {
    if (!restaurantId || !canSave) return;
    
    try {
      setSaving(true);
      
      // 선택된 상품들만 업데이트
      const updatedImageMap = new Map<string, string>();

      for (const product of selectedProducts) {
        const updateData = {
          name: product.name.trim(),
          description: product.description?.trim() || undefined,
          category: product.category?.trim() || undefined,
          originalPrice: product.originalPrice,
          stock: product.stock
        };

        const result = await sellerService.updateProduct(
          product.id,
          updateData,
          product.imageFile && user?.id
            ? { imageFile: product.imageFile }
            : undefined
        );
        if (!result.success) {
          throw new Error(result.error || `${product.name} 수정에 실패했습니다`);
        }

        if (result.imageUrl) {
          updatedImageMap.set(product.id, result.imageUrl);
        }
      }

      showToast(`${selectedProducts.length}개 상품이 성공적으로 수정되었습니다`);
      
      // 선택 해제
      setProducts(prev => prev.map(p => ({
        ...p,
        selected: false,
        imageFile: null,
        imageUrl: updatedImageMap.get(p.id) ?? p.imageUrl
      })));
      
      // 3초 후 내 가게로 이동
      setTimeout(() => {
        navigate('/seller/store');
      }, 3000);
      
    } catch (error) {
      console.error('Save products error:', error);
      showToast(error instanceof Error ? error.message : '저장 중 오류가 발생했습니다');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    
    try {
      setSaving(true);
      
      // 선택된 상품들 삭제
      for (const product of selectedProducts) {
        const result = await sellerService.deleteProduct(product.id);
        if (!result.success) {
          throw new Error(result.error || `${product.name} 삭제에 실패했습니다`);
        }
      }

      showToast(`${selectedProducts.length}개 상품이 성공적으로 삭제되었습니다`);
      
      // 삭제된 상품들을 목록에서 제거
      setProducts(prev => prev.filter(p => !selectedProducts.some(sp => sp.id === p.id)));
      setShowDeleteConfirm(false);
      
    } catch (error) {
      console.error('Delete products error:', error);
      showToast(error instanceof Error ? error.message : '삭제 중 오류가 발생했습니다');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/seller/store')}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-[32px] font-bold text-gray-800">상품 수정</h1>
            </div>

            {/* 검색 바 */}
            <div className="w-96">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="수정할 상품을 검색하세요"
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <svg
                  className="absolute right-4 top-3.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* 전체 선택 체크박스 */}
          {products.length > 0 && (
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-5 h-5 text-green-500 rounded focus:ring-2 focus:ring-green-500"
                />
                <span className="text-[16px] text-gray-600 font-medium">전체 선택</span>
              </label>
            </div>
          )}

          {/* 선택된 상품 수 표시 */}
          {selectedProducts.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-[16px] text-green-700 font-medium">
                  {selectedProducts.length}개 상품이 선택되었습니다
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Product List */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {filteredProducts.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                  <div className="text-gray-400 text-6xl mb-4">📦</div>
                  <p className="text-[18px] text-gray-500">
                    {searchTerm ? '검색 결과가 없습니다' : '등록된 상품이 없습니다'}
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => navigate('/seller/store/product-register')}
                      className="mt-4 px-6 py-3 bg-green-500 text-white text-[16px] font-semibold rounded-lg hover:bg-green-600 transition-colors"
                    >
                      첫 상품 등록하기
                    </button>
                  )}
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="p-6">
                      <ProductFormCard
                        product={product}
                        onChange={(updatedProduct) => handleProductChange(product.id, updatedProduct)}
                        selected={product.selected}
                        onSelect={(selected) => handleProductSelect(product.id, selected)}
                        disabled={false}
                        showCheckbox={true}
                        showDeleteButton={false}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Column - Actions & Summary */}
          <div className="space-y-6">
            {/* 선택 요약 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-[20px] font-semibold text-gray-800 mb-4">작업 요약</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">전체 상품</span>
                  <span className="text-[18px] font-semibold text-gray-800">{products.length}개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">검색 결과</span>
                  <span className="text-[18px] font-semibold text-gray-800">{filteredProducts.length}개</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[16px] text-gray-600">선택된 상품</span>
                  <span className="text-[18px] font-semibold text-green-600">{selectedProducts.length}개</span>
                </div>
              </div>
            </div>

            {/* 수정 가이드 */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-[20px] font-semibold text-gray-800 mb-4">수정 안내</h3>
              <div className="space-y-3 text-[14px] text-gray-600">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>수정할 상품을 체크박스로 선택하세요</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>선택된 상품들만 일괄 수정됩니다</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>삭제된 상품은 복구할 수 없습니다</p>
                </div>
              </div>
            </div>

            {/* 액션 버튼들 */}
            <div className="sticky top-6 space-y-4">
              <button
                onClick={handleSave}
                disabled={!canSave || saving}
                className={`w-full py-4 rounded-xl font-semibold text-[18px] transition-all ${
                  canSave && !saving
                    ? 'bg-green-500 text-white hover:bg-green-600 shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saving ? '저장 중...' : '선택 상품 수정'}
              </button>

              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={!canDelete || saving}
                className={`w-full py-4 rounded-xl font-semibold text-[18px] transition-all ${
                  canDelete && !saving
                    ? 'bg-red-500 text-white hover:bg-red-600 shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                선택 상품 삭제
              </button>

              {!canSave && !canDelete && (
                <p className="text-center text-[14px] text-gray-500">
                  수정하거나 삭제할 상품을 선택해주세요
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md mx-4">
            <h3 className="text-[24px] font-bold text-gray-800 mb-4">
              상품 삭제 확인
            </h3>
            <p className="text-[16px] text-gray-600 mb-8">
              선택한 {selectedProducts.length}개 상품을 삭제하시겠습니까?<br />
              <span className="font-semibold text-red-600">삭제된 상품은 복구할 수 없습니다.</span>
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="py-3 px-6 border border-gray-300 rounded-xl text-[16px] font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="py-3 px-6 bg-red-500 text-white text-[16px] font-semibold rounded-xl hover:bg-red-600 disabled:bg-gray-300 transition-colors"
              >
                {saving ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}
